import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import { 
  BarChart3, 
  TrendingUp, 
  Mail, 
  MessageSquare, 
  RefreshCw
} from 'lucide-react';

const NotificationAnalytics = () => {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [templateStats, setTemplateStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams();
      if (dateRange.startDate) queryParams.append('startDate', dateRange.startDate);
      if (dateRange.endDate) queryParams.append('endDate', dateRange.endDate);

      const response = await fetch(
        `${API_BASE_URL}/api/admin/notification-analytics/overview?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setOverview(data);
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const fetchTrends = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams();
      if (dateRange.startDate) queryParams.append('startDate', dateRange.startDate);
      if (dateRange.endDate) queryParams.append('endDate', dateRange.endDate);

      const response = await fetch(
        `${API_BASE_URL}/api/admin/notification-analytics/trends?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTrends(data);
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
    }
  }, [dateRange]);

  const fetchTemplateStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams();
      if (dateRange.startDate) queryParams.append('startDate', dateRange.startDate);
      if (dateRange.endDate) queryParams.append('endDate', dateRange.endDate);

      const response = await fetch(
        `${API_BASE_URL}/api/admin/notification-analytics/templates?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTemplateStats(data);
      }
    } catch (error) {
      console.error('Error fetching template stats:', error);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchOverview();
    fetchTrends();
    fetchTemplateStats();
  }, [fetchOverview, fetchTrends, fetchTemplateStats]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Notification Analytics</h2>
        <button
          onClick={() => {
            fetchOverview();
            fetchTrends();
            fetchTemplateStats();
          }}
          className="flex items-center px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                const end = new Date();
                const start = new Date();
                start.setDate(start.getDate() - 30);
                setDateRange({
                  startDate: start.toISOString().split('T')[0],
                  endDate: end.toISOString().split('T')[0]
                });
              }}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Last 30 Days
            </button>
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* SMS Stats */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-blue-500" />
                SMS Statistics
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Sent:</span>
                <span className="font-semibold">{overview.sms?.total || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Successful:</span>
                <span className="font-semibold text-green-600">{overview.sms?.sent || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Success Rate:</span>
                <span className="font-semibold">{overview.sms?.successRate?.toFixed(1) || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Cost:</span>
                <span className="font-semibold">KES {overview.sms?.totalCost?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg Cost:</span>
                <span className="font-semibold">KES {overview.sms?.avgCost?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>

          {/* Email Stats */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <Mail className="h-5 w-5 mr-2 text-green-500" />
                Email Statistics
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Sent:</span>
                <span className="font-semibold">{overview.email?.total || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Delivered:</span>
                <span className="font-semibold text-green-600">{overview.email?.delivered || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Opened:</span>
                <span className="font-semibold text-blue-600">{overview.email?.opened || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Success Rate:</span>
                <span className="font-semibold">{overview.email?.successRate?.toFixed(1) || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Delivery Rate:</span>
                <span className="font-semibold">{overview.email?.deliveryRate?.toFixed(1) || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Open Rate:</span>
                <span className="font-semibold">{overview.email?.openRate?.toFixed(1) || 0}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overall Stats */}
      {overview && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2 text-[#d44100]" />
            Overall Performance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-800">{overview.overall?.total || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Total Notifications</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">
                {overview.overall?.successRate?.toFixed(1) || 0}%
              </p>
              <p className="text-sm text-gray-600 mt-1">Overall Success Rate</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-orange-600">
                KES {overview.sms?.totalCost?.toFixed(2) || '0.00'}
              </p>
              <p className="text-sm text-gray-600 mt-1">Total SMS Cost</p>
            </div>
          </div>
        </div>
      )}

      {/* Trends */}
      {trends && trends.trends && trends.trends.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-[#d44100]" />
            Trends Over Time
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SMS Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SMS Sent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email Sent</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {trends.trends.map((trend, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm text-gray-900">{trend.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{trend.sms?.total || 0}</td>
                    <td className="px-4 py-3 text-sm text-green-600">{trend.sms?.sent || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{trend.email?.total || 0}</td>
                    <td className="px-4 py-3 text-sm text-green-600">{trend.email?.sent || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Template Performance */}
      {templateStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* SMS Templates */}
          {templateStats.sms && templateStats.sms.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">SMS Template Performance</h3>
              <div className="space-y-3">
                {templateStats.sms.map((template) => (
                  <div key={template.id} className="border-b border-gray-200 pb-3 last:border-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{template.name}</p>
                        <p className="text-xs text-gray-500">Used {template.usageCount} times</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-green-600">
                          {template.successRate?.toFixed(1) || 0}%
                        </p>
                        <p className="text-xs text-gray-500">
                          {template.sent}/{template.total}
                        </p>
                      </div>
                    </div>
                    {template.totalCost > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Cost: KES {template.totalCost.toFixed(2)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email Templates */}
          {templateStats.email && templateStats.email.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Email Template Performance</h3>
              <div className="space-y-3">
                {templateStats.email.map((template) => (
                  <div key={template.id} className="border-b border-gray-200 pb-3 last:border-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{template.name}</p>
                        <p className="text-xs text-gray-500">Used {template.usageCount} times</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-green-600">
                          {template.successRate?.toFixed(1) || 0}%
                        </p>
                        <p className="text-xs text-gray-500">
                          {template.sent}/{template.total}
                        </p>
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      <span>Delivery: {template.deliveryRate?.toFixed(1) || 0}%</span>
                      {' | '}
                      <span>Open: {template.openRate?.toFixed(1) || 0}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="text-center py-8 text-gray-500">Loading analytics...</div>
      )}
    </div>
  );
};

export default NotificationAnalytics;













